<template>
    <b-modal title-tag="h2" title="New Form" :lazy="true" :visible="isOpen">
        <form>
            <b-form-group label-cols="3" label="Template:" label-for="template">
                <vue-select
                    v-model="data.template"
                    label="name"
                    name="name"
                    :placeholder="templatePlaceholder"
                    :disabled="isLoading"
                    :options="templateOptions"
                    class="show-actions"
                    v-validate="'required'"
                    :class="{
                        'border-danger': errors.has('name')
                    }"
                >
                    <template v-slot:list-footer>
                        <li
                            ref="footerLoader"
                            class="loader"
                            v-if="hasNextPage"
                        >
                            Loading...
                        </li>
                    </template>
                </vue-select>
            </b-form-group>

            <b-form-group label-cols="3" label="Title:" label-for="title">
                <b-form-input
                    v-model="data.title"
                    name="title"
                    placeholder="Title (required)"
                    v-validate="'required'"
                    :class="{
                        'border-danger': errors.has('title') || isTitleExceed
                    }"
                />
                <span
                    v-bind:style="[
                        titleCount >= 0 ? { color: 'grey' } : { color: 'red' }
                    ]"
                    style="font-size: 10px;"
                    class="mt-1 d-block text-right"
                >
                    {{ titleCount }} character(s) remaining
                </span>
            </b-form-group>

            <b-form-group
                v-if="module === MODULE_CLIENT"
                label-cols="3"
                label="Patient:"
                label-for="patient"
            >
                <vue-select
                    id="patient"
                    ref="vSelectPatient"
                    :class="{ 'border-danger': errors.has('patient') }"
                    :options="patientOptions"
                    v-model="data.selectedPatient"
                    label="name"
                    name="patient"
                    aria-placeholder="true"
                    aria-expanded="true"
                    placeholder="Patient name (optional)"
                ></vue-select>
            </b-form-group>

            <b-form-group
                v-if="module === MODULE_PATIENT"
                label-cols="3"
                label="Client:"
                label-for="client"
            >
                <vue-select
                    id="client"
                    ref="vSelectClient"
                    v-validate="'required'"
                    :class="{ 'border-danger': errors.has('client') }"
                    :options="clientOptions"
                    v-model="data.selectedClient"
                    name="client"
                    aria-placeholder="true"
                    aria-expanded="true"
                    placeholder="Client name (required)"
                ></vue-select>
            </b-form-group>
        </form>

        <template v-slot:modal-footer>
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button v-if="!isLoading" variant="primary" @click="save">
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import vueSelect from "vue-select";
import { uniqBy, concat } from "lodash";
import Patient from "@/services/patient";
import TemplateService from "@/services/templates";
import ClientWebform from "@/services/client-webform";
import Loader from "@/module/commons/loader";
import TEMPLATES from "@/constants/templates";

const PER_PAGE = 500;
const MODULE_CLIENT = "client";
const MODULE_PATIENT = "patient";

export default {
    components: {
        vueSelect,
        Loader
    },
    computed: {
        module() {
            let module = "client";
            if (
                this.$route.name == "patient_profile" ||
                this.$route.name == "soap"
            ) {
                // The soap module will have same form fields in patient module
                module = "patient";
            }
            return module;
        },
        templatePlaceholder() {
            return this.isLoading && this.templateOptions.length == 0
                ? "Loading..."
                : "Select template (required)";
        },
        clientId() {
            return this.client
                ? this.client.id
                : this.data.selectedClient
                ? this.data.selectedClient.id
                : null;
        },
        patientId() {
            return this.patient
                ? this.patient.id
                : this.data.selectedPatient
                ? this.data.selectedPatient.id
                : null;
        }
    },
    props: {
        patient: {
            type: Object,
            default: null
        },
        client: {
            type: Object,
            default: null
        },
        soapId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            MODULE_CLIENT,
            MODULE_PATIENT,
            isLoading: false,
            isOpen: false,
            templateOptions: [],
            patientOptions: [],
            clientOptions: [],
            hasNextPage: false,
            page: 1,
            isTitleExceed: false,
            titleCount: 100,
            data: {
                template: null,
                title: "",
                selectedPatient: null,
                selectedClient: null
            }
        };
    },
    watch: {
        "data.template"(value) {
            if (value) {
                this.data.title = value.name || "";
            } else {
                this.data.title = "";
            }
        },
        "data.title"(value) {
            let valueLength = 0;
            if (value) {
                valueLength = value.length;
            }
            this.titleCount = 100 - valueLength;

            if (this.titleCount < 0) {
                this.isTitleExceed = true;
            } else {
                this.isTitleExceed = false;
            }
        }
    },
    methods: {
        open() {
            this.isOpen = true;
            this.fetchDropdownList();

            if (this.module === MODULE_PATIENT) {
                this.getOwners();
            } else if (this.module === MODULE_CLIENT) {
                this.getPatients();
            }
        },
        close() {
            this.isOpen = false;
        },
        async fetchDropdownList() {
            this.isLoading = true;

            let params = {
                perPage: PER_PAGE,
                page: this.page,
                orderBy: "name",
                order: "asc",
                type: parseInt(TEMPLATES.TYPE.WEBFORM_TEMPLATE)
            };
            const results = await TemplateService.getTemplates(params);
            this.templateOptions = uniqBy(
                concat(this.templateOptions, results.resources),
                "id"
            );

            this.hasNextPage = results.meta.currentPage < results.meta.lastPage;
            this.page++;
            if (this.hasNextPage) {
                await this._sleep(100);
                this.fetchDropdownList();
            }

            this.isLoading = false;
        },
        getPatients() {
            let params = {
                search_key: null,
                id: this.clientId,
                all_status: 1
            };
            Patient.getPatientsByClient(params).then(response => {
                this.patientOptions = response;
            });
        },
        async getOwners() {
            const owners = this.patient.active_owners;
            if (owners.length > 0) {
                let result = [];

                for (const record of owners) {
                    const isPrimaryOwner = parseInt(record.pivot.isprimary);
                    let opt = {
                        label: record.label,
                        id: record.id,
                        value: record.id,
                        isPrimaryOwner: isPrimaryOwner
                    };
                    result.push(opt);

                    if (isPrimaryOwner == 1 && this.module == MODULE_PATIENT) {
                        this.data.selectedClient = opt;
                    }
                }
                this.clientOptions = result;
            }
        },
        async save() {
            let self = this;
            try {
                this.isLoading = true;
                let valid = await self.$validator.validateAll();

                if (!valid || this.isTitleExceed) {
                    this.isLoading = false;
                    return;
                }

                const result = await ClientWebform.saveWebform({
                    client_id: this.clientId,
                    patient_id: this.patientId,
                    template_id: this.data.template.id,
                    soap_id: this.soapId,
                    title: this.data.title
                });

                if (result) {
                    this.isLoading = false;
                    this.close();
                    this._message("Form created");

                    if (this.module === MODULE_PATIENT) {
                        this.$router.push({
                            name: "patient_webform",
                            params: { id: result.encoded_id }
                        });
                    } else if (this.module === MODULE_CLIENT) {
                        this.$router.push({
                            name: "client_webform",
                            params: { id: result.encoded_id }
                        });
                    }
                } else {
                    throw "Error";
                }
            } catch (e) {
                this._message("Error: Unable to save", "danger");
                this.isLoading = false;
            }
        }
    }
};
</script>
